import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
	"relative inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[background-color,border-color,color,opacity] duration-100 outline-none select-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50 aria-invalid:ring-destructive/30 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
	{
		variants: {
			variant: {
				default: "bg-primary text-primary-foreground hover:bg-primary/88",
				secondary: "bg-secondary text-secondary-foreground hover:bg-accent",
				outline: "border bg-card hover:bg-accent hover:text-accent-foreground",
				ghost: "hover:bg-accent hover:text-accent-foreground",
				destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
				link: "text-foreground underline-offset-4 hover:underline",
			},
			size: {
				default: "h-8 px-3",
				sm: "h-7 gap-1.5 rounded-md px-2.5 text-[13px]",
				lg: "h-10 px-5",
				icon: "size-8",
				"icon-sm": "size-7",
			},
		},
		defaultVariants: { variant: "default", size: "default" },
	},
);

type ButtonProps = React.ComponentProps<"button"> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
		/** Disables the button and swaps its leading icon for a spinner. Width stays stable. */
		loading?: boolean;
	};

function Button({
	className,
	variant,
	size,
	asChild = false,
	loading = false,
	disabled,
	children,
	...props
}: ButtonProps) {
	const Comp = asChild ? Slot : "button";
	return (
		<Comp
			data-slot="button"
			data-loading={loading || undefined}
			aria-busy={loading || undefined}
			disabled={disabled || loading}
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		>
			{asChild ? (
				children
			) : (
				<>
					{loading && <Spinner className="absolute" />}
					<span className={cn("contents", loading && "text-transparent [&>*]:invisible")}>
						{children}
					</span>
				</>
			)}
		</Comp>
	);
}

export { Button, buttonVariants };
